import ky, { HTTPError } from 'ky'
import CryptoJS from 'crypto-js'
import type { NormalizedOptions, Options } from 'ky'
import { authService } from '@/services'
import { env } from '@/env'
import { router } from '@/main'

export function isHttpError<T = unknown>(error: unknown): error is HTTPError<T>
export function isHttpError<T = unknown>(
  error: unknown,
  status: number | Array<number>,
): error is HTTPError<T>
export function isHttpError<T = unknown>(
  error: unknown,
  status?: number | Array<number>,
): error is HTTPError<T> {
  return (
    error instanceof HTTPError &&
    (status === undefined ||
      (Array.isArray(status)
        ? status.includes(error.response.status)
        : error.response.status === status))
  )
}

let refreshing: Promise<void> | null = null

const apiBaseUrl = env.VITE_APP_API_URL

const refreshToken = async () => {
  try {
    const response = await ky.post(
      `${apiBaseUrl}/api/ad_hoc/v1/refresh_token`,
      {
        headers: {
          Authorization: `Bearer ${authService.refreshToken}`,
        },
      },
    )

    const newAccessToken = response.headers.get('token')
    const newRefreshToken = response.headers.get('refresh_token')

    if (newAccessToken) authService.accessToken = newAccessToken
    if (newRefreshToken) authService.refreshToken = newRefreshToken
  } catch (error) {
    router.navigate({ to: '/auth/logout', replace: true })
    throw error
  }
}

const http = ky.create({
  prefixUrl: apiBaseUrl,
  hooks: {
    beforeRequest: [
      (request) => {
        if (authService.accessToken) {
          request.headers.set(
            'Authorization',
            `Bearer ${authService.accessToken}`,
          )
        }
      },
    ],
    afterResponse: [
      async (_request, _options, response) => {
        const newAccessToken = response.headers.get('token')
        const newRefreshToken = response.headers.get('refresh_token')

        if (newAccessToken) authService.accessToken = newAccessToken
        if (newRefreshToken) authService.refreshToken = newRefreshToken

        if (
          response.headers.has('per-page') &&
          response.headers.has('total') &&
          response.headers.has('page') &&
          response.headers.has('last')
        ) {
          const resJson = await response.json<Record<string, unknown>>()
          resJson.meta = {
            per_page: Number(response.headers.get('per-page')),
            total_count: Number(response.headers.get('total')),
            current_page: Number(response.headers.get('page')),
            last_page: Number(response.headers.get('last')),
            next_page:
              Number(response.headers.get('last')) >
              Number(response.headers.get('page'))
                ? Number(response.headers.get('page')) + 1
                : null,
          }
          return new Response(JSON.stringify(resJson), response)
        }

        return response
      },

      async (request, options: NormalizedOptions, response) => {
        if (response.status !== 401) return response

        if (options.retrying || options.strictAuth === false) return response

        if (!authService.refreshToken) {
          return router.navigate({ to: '/auth/logout', replace: true })
        }

        if (!refreshing) {
          refreshing = refreshToken().finally(() => {
            refreshing = null
          })
        }

        try {
          await refreshing
        } catch (e) {
          return response
        }

        return ky(request, {
          ...options,
          // @ts-expect-error
          retrying: true,
          headers: {
            ...options.headers,
            Authorization: `Bearer ${authService.accessToken}`,
          },
        })
      },
    ],
  },
})

export const httpClient = http.extend((options: Options) => ({
  prefixUrl: `${options.prefixUrl}/api/ad_hoc/v1/`,
}))

export const httpClientUnauthenticated = httpClient.extend({
  hooks: {
    beforeRequest: [
      (request) => {
        request.headers.delete('Authorization')
      },
    ],
  },
})

export const httpClientHeaderMac = httpClient.extend({
  headers: {
    'Mac-Address': CryptoJS.MD5(navigator.userAgent).toString(),
  },
})
